// An image's caption, set over its bottom-left corner on a scrim. It follows
// the main app's Library photo cards (src/app/components/ThoughtCard.tsx, the
// picture-only card): a to-top dark gradient (black/60 fading out), 12px of
// padding, a serif title at ~14px/1.3 and a ~11px meta line at 80%, clipped
// to the card's corners. In Writings' tokens: an ink scrim, the caption in
// Newsreader italic on paper, and the capture stamp in the opposite corner.
// The scrim hugs the caption (a 48px fade above it) rather than darkening half
// of a tall photo, and is a touch deeper than the Library's with a soft text
// shadow, so a caption stays legible over a light photo.
//
// It's absolutely placed: the figure it's in must be `relative`, and exactly
// the photo's size (nothing else in its flow).

/** The overlay's box: the bottom of the image, rounded like it (4px, Composer lab's photo corners). */
export const OVERLAY_CLS =
  'absolute inset-x-0 bottom-0 flex flex-wrap items-end gap-x-3 gap-y-0.5 rounded-b-[4px] px-3 pb-3 pt-12 ' +
  'bg-[linear-gradient(to_top,rgba(45,27,30,.76),rgba(45,27,30,.42)_55%,rgba(45,27,30,0))]'

const SHADOW = '[text-shadow:0_1px_2px_rgba(45,27,30,.5)]'

/** The caption's type: 15px in the column, 14px on a wrap (the sizes captions always had). */
export function captionCls(small: boolean): string {
  return `min-w-0 flex-[1_1_60%] font-display text-on-scrim ${small ? 'text-[14px]/[1.3]' : 'text-[15px]/[1.3]'} ${SHADOW}`
}

export const STAMP_CLS = `ml-auto shrink-0 font-sans text-[11px]/[1.3] font-medium text-on-scrim/80 ${SHADOW}`

/** A read-only caption overlay (reader, History). Renders nothing with neither a caption nor a stamp. */
export function CaptionOverlay({ caption, stamp, small = false }: { caption?: string; stamp?: string | null; small?: boolean }) {
  const text = (caption ?? '').trim()
  if (!text && !stamp) return null
  return (
    <figcaption data-caption-overlay="" className={OVERLAY_CLS}>
      {text && <span className={`${captionCls(small)} [text-wrap:pretty]`}>{text}</span>}
      {stamp && <span className={STAMP_CLS}>{stamp}</span>}
    </figcaption>
  )
}
